import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowUpRight, Building2, ExternalLink, Landmark, Newspaper, RefreshCw, Search } from "lucide-react";
import { useApi, useAuth, useInvalidateOn } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation, geographyText } from "@ncp/i18n";
import { Badge, Button, Card, EmptyState, Input, PageHeader, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, Skeleton, StatCard, cn, toast, InfoHint, SourceText, useSave } from "@ncp/ui";
import { NEWS_SYNCERS, newsKeys, useNews, useNewsItem, type NewsCompanyCard, type NewsKind, type NewsSummary, type NewsSyncResult } from "./api";

const KINDS: NewsKind[] = ["Intelligence", "Deal", "Market", "Press"];

function readFilters(params: URLSearchParams) {
  return { source: params.get("source") ?? "", kind: params.get("kind") ?? "", q: params.get("q") ?? "", window: params.get("window") ?? "" };
}

function sourceLabel(key: string, t: (k: string, o?: Record<string, unknown>) => string) {
  return t(`backoffice:news.sources.${key}`, { defaultValue: key });
}

/** A publisher without an Arabic name keeps the name it publishes under, marked as English. */
function SourceName({ source }: { source: string }) {
  const { t } = useTranslation();
  const label = t(`backoffice:news.sources.${source}`, { defaultValue: "" });
  return label ? <>{label}</> : <bdi lang="en" dir="ltr">{source}</bdi>;
}

function KindBadge({ kind }: { kind: string }) {
  const { t } = useTranslation();
  const variant = kind === "Intelligence" ? "ai" : kind === "Deal" ? "gold" : kind === "Market" ? "info" : "outline";
  return <Badge size="sm" variant={variant}>{t(`backoffice:news.kinds.${kind}`, { defaultValue: kind })}</Badge>;
}

function SourceBadge({ source }: { source: string }) {
  return <Badge size="sm" variant={source === "mergermarket" ? "gold" : source === "marketfeed" ? "info" : "outline"}><SourceName source={source} /></Badge>;
}

function NewsDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const detail = useNewsItem(id);
  const n = detail.data?.item;
  const deal = detail.data?.deal;
  const body = detail.data?.body ?? n?.summary;

  return (
    <Sheet open={id !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="end" width="max-w-2xl">
        <SheetHeader>
          <SheetTitle className="pe-8">{n?.headline ? <SourceText text={n.headline} /> : t("backoffice:news.item")}</SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            {n ? <><SourceBadge source={n.sourceKey} /><KindBadge kind={n.kind} /><span className="font-mono text-xs">{n.externalId}</span><span>{formatDateTime(n.publishedAt, locale)}</span></> : null}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-6">
          {detail.isLoading || !n ? <Skeleton className="h-64" /> : (
            <>
              {n.intelligenceType ? <p className="text-xs uppercase tracking-wide text-muted-foreground"><SourceText text={`${n.intelligenceType}${n.sizeBand ? ` · ${n.sizeBand}` : ""}`} /></p> : null}
              {body ? <p className="text-sm leading-relaxed" dir="auto"><SourceText text={body} /></p> : null}
              {n.companies.length > 0 ? (
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("backoffice:news.companies")}</p>
                  <div className="flex flex-wrap gap-1.5">{n.companies.map((c) => <Badge key={c} size="sm" variant="outline"><Building2 /> <SourceText text={c} /></Badge>)}</div>
                </div>
              ) : null}
              {n.countries.length > 0 || n.sectors.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {n.countries.map((c) => <Badge key={c} size="sm" variant="outline"><SourceText text={geographyText(c, locale)} /></Badge>)}
                  {n.sectors.map((s) => <Badge key={s} size="sm" variant="outline"><SourceText text={s} /></Badge>)}
                </div>
              ) : null}
              {deal ? (
                <Card className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("backoffice:news.relatedDeal")}</p>
                  <p className="mt-1 font-medium"><SourceText text={deal.title} /></p>
                  <p className="mt-1 text-xs text-muted-foreground font-mono">{deal.externalId} · {t(`backoffice:dealWatch.status.${deal.status}`, { defaultValue: deal.status })} · {t(`backoffice:dealWatch.filing.${deal.filingStatus}`, { defaultValue: deal.filingStatus })}</p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate(`/investigations?tab=deal-watch&deal=${deal.id}`)}><Landmark /> {t("backoffice:news.openDealWatch")}</Button>
                </Card>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <SourceBadge source={n.sourceKey} />
                {n.url ? <Button size="sm" variant="outline" asChild><a href={n.url} target="_blank" rel="noreferrer"><ExternalLink /> {t("backoffice:news.openSource")}</a></Button> : <span className="text-xs text-muted-foreground">{t("backoffice:news.noUrl")}</span>}
              </div>
              {n.url ? <p className="break-all text-[11px] text-muted-foreground">{n.url}</p> : null}
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function NewsRow({ item, selected, onSelect }: { item: NewsSummary; selected: boolean; onSelect: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (
    <article className={cn("relative w-full rounded-2xl border p-4 text-start transition-colors hover:border-aegold-400 hover:bg-sand-50/80 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-aegold-500", selected ? "border-aegold-500 bg-sand-50" : "border-sand-200 bg-white")}>
      <div className="flex flex-wrap items-center gap-2">
        <SourceBadge source={item.sourceKey} />
        <KindBadge kind={item.kind} />
        {item.intelligenceType ? <span className="text-[11px] text-muted-foreground"><SourceText text={item.intelligenceType} /></span> : null}
        <span className="ms-auto text-[11px] text-muted-foreground">{formatDateTime(item.publishedAt, locale)}</span>
      </div>
      <p className="mt-2 font-medium leading-snug" dir="auto"><button type="button" onClick={onSelect} aria-pressed={selected} className="text-start outline-none after:absolute after:inset-0 after:rounded-2xl"><SourceText text={item.headline} /></button></p>
      {item.summary ? <p className="mt-1 line-clamp-2 text-sm text-muted-foreground" dir="auto"><SourceText text={item.summary} /></p> : null}
      {item.companies.length > 0 ? <p className="mt-2 text-xs text-muted-foreground"><SourceText text={item.companies.slice(0, 4).join(" · ")} />{item.companies.length > 4 ? ` · +${item.companies.length - 4}` : ""}</p> : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {item.url ? <a href={item.url} target="_blank" rel="noreferrer" className="relative z-10 inline-flex items-center gap-1 text-[11px] font-medium text-aegold-800 hover:underline"><ExternalLink className="size-3" /> {t("backoffice:news.openSource")}</a> : null}
        {item.relatedDealId ? <p className="text-[11px] text-aegold-800">{t("backoffice:news.hasDeal")}</p> : null}
      </div>
    </article>
  );
}

/** Platform-wide News: Mergermarket is one source; the Market Monitor feed is another. */
export function NewsPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canSync = (user?.roles ?? []).some((r) => NEWS_SYNCERS.includes(r));
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const selected = params.get("item");

  const patch = (next: Record<string, string | null>) => setParams((p) => {
    for (const [k, v] of Object.entries(next)) {
      if (v == null || v === "") p.delete(k);
      else p.set(k, v);
    }
    return p;
  });

  const news = useNews({ source: filters.source || undefined, kind: filters.kind || undefined, q: filters.q.trim() || undefined, window: filters.window || undefined });
  useInvalidateOn(["dealwatch.updated", "dealwatch.candidates", "signal.created"], [[...newsKeys.all]]);
  const view = news.data;

  const sync = useSave({
    mutationFn: () => api.post<NewsSyncResult>("/api/news/sync", {}),
    // A sync that ran with errors is not "complete": what it stored is counted, and what failed is listed as a warning (S 2.8).
    saved: (r) => (r.queued || r.errors?.length ? null : t("backoffice:news.syncDone", { fetched: r.fetched ?? 0, inserted: r.inserted ?? 0, updated: r.updated ?? 0 })),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: newsKeys.all });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      if (r.queued) toast.info(t("backoffice:news.syncQueued"));
      else if (r.errors?.length) toast.warning(t("backoffice:news.syncPartial", { fetched: r.fetched ?? 0, inserted: r.inserted ?? 0, updated: r.updated ?? 0 }), { description: r.errors.join("; "), duration: 15_000 });
    },
  });

  return (
    <div>
      <PageHeader
        title={<span className="inline-flex items-center gap-2">{t("nav.news")}<InfoHint>{t("backoffice:news.subtitle")}</InfoHint></span>}
       
        actions={
          <span className="flex flex-wrap items-center gap-2">
            {view?.lastSync ? <span className="text-xs text-muted-foreground">{t("backoffice:news.lastSync")} {formatDateTime(view.lastSync, locale)}</span> : <span className="text-xs text-muted-foreground">{t("backoffice:news.neverSynced")}</span>}
            {canSync ? <Button size="sm" variant="outline" loading={sync.isPending} onClick={() => sync.mutate()}><RefreshCw /> {t("backoffice:news.syncNow")}</Button> : null}
          </span>
        }
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t("backoffice:news.stats.total")} value={view?.stats.total ?? 0} icon={Newspaper} footer={t("backoffice:news.stats.today", { count: view?.stats.today ?? 0 })} tone="gold" />
        <StatCard label={sourceLabel("mergermarket", t)} value={view?.stats.mergermarket ?? 0} icon={Landmark} footer={t("backoffice:news.stats.intelligence", { count: view?.stats.intelligence ?? 0 })} tone="sea" />
        <StatCard label={sourceLabel("marketfeed", t)} value={view?.stats.marketfeed ?? 0} icon={ArrowUpRight} footer={t("backoffice:news.stats.market", { count: view?.stats.market ?? 0 })} tone="camel" />
        <StatCard label={t("backoffice:news.stats.deals")} value={view?.stats.deal ?? 0} icon={Building2} footer={t("backoffice:news.stats.lastMonth", { count: view?.stats.lastMonth ?? 0 })} tone="neutral" />
      </div>

      {(view?.briefing?.companies.length ?? 0) > 0 ? (
        <section className="mb-6">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">{t("backoffice:news.topCompanies")}</h2>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {view!.briefing!.companies.slice(0, 12).map((c) => <CompanyCard key={c.id} company={c} />)}
          </div>
        </section>
      ) : null}

      {(view?.briefing?.lastMonthMergers.length ?? 0) > 0 && !filters.window ? (
        <section className="mb-6">
          <h2 className="mb-2 text-sm font-semibold">{t("backoffice:news.lastMonthMergers")}</h2>
          <div className="grid gap-2">
            {view!.briefing!.lastMonthMergers.slice(0, 8).map((item) => <NewsRow key={`m-${item.id}`} item={item} selected={selected === item.id} onSelect={() => patch({ item: item.id })} />)}
          </div>
        </section>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button size="sm" variant={!filters.source ? "default" : "ghost"} onClick={() => patch({ source: null })}>{t("backoffice:news.allSources")}</Button>
        {(view?.sources ?? []).map((s) => (
          <Button key={s.key} size="sm" variant={filters.source === s.key ? "default" : "ghost"} onClick={() => patch({ source: filters.source === s.key ? null : s.key })}>
            <SourceName source={s.key} /> <Badge size="sm" variant="outline">{s.count}</Badge>
          </Button>
        ))}
        <span className="hidden h-5 w-px bg-sand-300 sm:block" />
        {KINDS.map((k) => (
          <Button key={k} size="sm" variant={filters.kind === k ? "default" : "ghost"} onClick={() => patch({ kind: filters.kind === k ? null : k })}>
            {t(`backoffice:news.kinds.${k}`)}
          </Button>
        ))}
        <Button size="sm" variant={filters.window === "lastMonth" ? "default" : "outline"} onClick={() => patch({ window: filters.window === "lastMonth" ? null : "lastMonth" })}>
          {t("backoffice:news.lastMonth")}
        </Button>
        <div className="relative ms-auto min-w-64">
          <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="px-9" defaultValue={filters.q} onKeyDown={(e) => { if (e.key === "Enter") patch({ q: e.currentTarget.value.trim() || null }); }} onBlur={(e) => patch({ q: e.currentTarget.value.trim() || null })} placeholder={t("backoffice:news.searchPlaceholder")} dir="auto" />
        </div>
      </div>

      {news.isLoading ? <Skeleton className="h-96" /> : (view?.items.length ?? 0) === 0 ? (
        <EmptyState icon={Newspaper} title={t("backoffice:news.empty")} description={t("backoffice:news.emptyHint")} />
      ) : (
        <div className="grid gap-3">
          {view!.items.map((item) => <NewsRow key={item.id} item={item} selected={selected === item.id} onSelect={() => patch({ item: item.id })} />)}
        </div>
      )}

      <NewsDrawer id={selected} onClose={() => patch({ item: null })} />
    </div>
  );
}

function CompanyCard({ company }: { company: NewsCompanyCard }) {
  const { t } = useTranslation();
  return (
    <article className="rounded-2xl border border-sand-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <SourceBadge source={company.sourceKey} />
        {company.sectors[0] ? <span className="text-[11px] text-muted-foreground"><SourceText text={company.sectors[0]} /></span> : null}
      </div>
      <p className="mt-1.5 font-medium leading-snug"><SourceText text={company.name} /></p>
      {company.summary ? <p className="mt-1 line-clamp-2 text-xs text-muted-foreground"><SourceText text={company.summary} /></p> : null}
      {company.url ? <a href={company.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-aegold-800 hover:underline"><ExternalLink className="size-3" /> {t("backoffice:news.openSource")}</a> : null}
    </article>
  );
}
